import { useState, useCallback, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, Buildings, Fire } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import AINavBar from "@/components/AINavBar";
import { useInfiniteFeed } from "@/hooks/useInfiniteFeed";
import { fetchAISection } from "@/lib/api";
const COMPANY_CATEGORIES = ["All", "Foundation Models", "Open Source", "AI Search", "Enterprise AI", "AI Agents", "AI Infrastructure"];
import { stripHtml } from "@/lib/stripHtml";

function SkeletonCard() {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 animate-pulse">
      <div className="flex gap-4 mb-4">
        <div className="w-12 h-12 bg-slate-200 rounded-xl shrink-0" />
        <div className="flex-1">
          <div className="h-4 bg-slate-200 rounded w-1/2 mb-2" />
          <div className="h-3 bg-slate-200 rounded w-1/3" />
        </div>
      </div>
      <div className="h-3 bg-slate-200 rounded w-full mb-2" />
      <div className="h-3 bg-slate-200 rounded w-2/3" />
    </div>
  );
}

function CompanyCard({ company }) {
  const slug = company.slug || company.id;
  return (
    <Link to={`/trending-ai/companies/${slug}`} className="block bg-white border border-slate-200 rounded-2xl p-5 hover:border-blue-300 hover:shadow-md transition-all group">
      <div className="flex items-start gap-4 mb-4">
        <div className="text-4xl shrink-0">{company.logo || "🤖"}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="text-base font-bold text-slate-900 group-hover:text-blue-600 transition-colors">{stripHtml(company.name)}</h3>
            {company.hot && (
              <span className="text-[10px] font-bold bg-red-500 text-white px-1.5 py-0.5 rounded flex items-center gap-0.5">
                <Fire size={7} weight="fill" /> HOT
              </span>
            )}
            {company.category && (
              <span className="text-[10px] font-semibold bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200">
                {company.category}
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500">{stripHtml(company.hq)} · Founded {company.founded}</p>
        </div>
      </div>

      <p className="text-sm text-slate-600 leading-relaxed mb-4">{stripHtml(company.description)}</p>

      <div className="grid grid-cols-2 gap-2 mb-4">
        {[
          { label: "Valuation", value: company.valuation },
          { label: "Total Funding", value: company.totalFunding || company.total_funding },
          { label: "Employees", value: company.employees },
          { label: "Stage", value: company.stage },
        ].map(({ label, value }) => value ? (
          <div key={label} className="bg-slate-50 rounded-lg p-2">
            <div className="text-xs font-bold text-slate-800 truncate">{value}</div>
            <div className="text-[10px] text-slate-400">{label}</div>
          </div>
        ) : null)}
      </div>

      {company.products && company.products.length > 0 && (
        <div className="mb-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Key Products</p>
          <div className="flex flex-wrap gap-1">
            {company.products.slice(0, 4).map((p) => (
              <span key={p} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{p}</span>
            ))}
            {company.products.length > 4 && (
              <span className="text-[10px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">+{company.products.length - 4} more</span>
            )}
          </div>
        </div>
      )}

      {company.recentNews && (
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 mb-3">
          <p className="text-[10px] font-semibold text-blue-600 uppercase tracking-wider mb-1">Latest</p>
          <p className="text-xs text-slate-700">{stripHtml(company.recentNews)}</p>
        </div>
      )}

      {company.ceo && (
        <div className="flex items-center justify-between text-xs text-slate-400 border-t border-slate-100 pt-3">
          <span>CEO: <span className="text-slate-600 font-medium">{company.ceo}</span></span>
          <span>{company.employees}</span>
        </div>
      )}
    </Link>
  );
}

export default function AICompaniesPage() {
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("All");
  const [sort, setSort] = useState("rank");

  const fetchFn = useCallback(
    (page) => fetchAISection("companies", { page, page_size: 12 }),
    []
  );
  const { items: apiItems, loading, loadingMore, hasMore, error, loadMore } = useInfiniteFeed({
    fetchFn,
    feedKey: "ai-companies",
    getCacheKey: (page) => `ai-companies-p${page}`,
    ttlMs: 15 * 60 * 1000,
  });

  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) loadMore(); }, { rootMargin: "300px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  const allItems = apiItems;

  const filtered = allItems
    .filter((c) => {
      const matchCat = cat === "All" || c.category === cat;
      const matchSearch = !search || c.name.toLowerCase().includes(search.toLowerCase()) || (c.description || "").toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    })
    .sort((a, b) => {
      if (sort === "name") return a.name.localeCompare(b.name);
      return (a.rank || 99) - (b.rank || 99);
    });

  return (
    <>
      <SEO
        title="AI Companies — Directory of Top AI Companies · TrendMing"
        description="Profiles of OpenAI, Anthropic, Google DeepMind, Meta AI, xAI, Mistral, Cohere, Perplexity and more. Funding, products, team, and latest news."
        canonical="https://trendming.com/trending-ai/companies"
      />
      <AINavBar />
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        <div className="mb-6">
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">AI Intelligence · Companies</span>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">AI Company Directory</h1>
          <p className="text-slate-500 text-sm mt-1">Comprehensive profiles of the companies shaping artificial intelligence.</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search companies…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
            />
          </div>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30">
            <option value="rank">By Rank</option>
            <option value="name">A–Z</option>
          </select>
        </div>

        <div className="flex items-center gap-2 flex-wrap mb-6">
          {COMPANY_CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                cat === c ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-4 mb-5 text-xs text-slate-500">
          <span>{filtered.length} companies</span>
          <span>·</span>
          <span>{allItems.filter((c) => c.hot).length} trending this week</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {loading && Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
          {!loading && filtered.map((company) => <CompanyCard key={company.id || company.slug} company={company} />)}
          {!loading && filtered.length === 0 && (
            <div className="col-span-full text-center py-16 text-slate-400">
              <p className="text-lg font-medium">No companies found</p>
            </div>
          )}
        </div>

        {hasMore && !loading && <div ref={sentinelRef} className="h-4" />}
        {loadingMore && <div className="text-center py-4 text-xs text-slate-400">Loading more…</div>}
        {error && !loading && (
          <div className="text-center py-8 text-slate-500">
            <p className="text-red-500 font-medium">Could not load companies</p>
            <p className="text-xs mt-1">Content is being refreshed — check back shortly.</p>
          </div>
        )}
      </div>
    </>
  );
}
